import { computed, ref } from 'vue'
import { api, ApiError } from '../api/client'

// 模块级单例：任何组件 import 后拿到的都是同一份状态。
// 这是最轻量的全局 store；规模变大再迁移到 Pinia，思路一致。
const polls = ref([])
const loading = ref(false)
const error = ref(null)
const pendingPollIds = ref(new Set())

function markPending(pollId, isPending) {
  const next = new Set(pendingPollIds.value)
  isPending ? next.add(pollId) : next.delete(pollId)
  pendingPollIds.value = next
}

// 用服务端返回的最新对象替换本地那一条，避免前后端票数不一致。
function replacePoll(updated) {
  polls.value = polls.value.map((poll) => (poll.id === updated.id ? updated : poll))
}

export function usePolls() {
  async function refresh() {
    loading.value = true
    error.value = null
    try {
      polls.value = await api.listPolls()
    } catch (err) {
      error.value = err instanceof ApiError ? err.message : '加载失败'
    } finally {
      loading.value = false
    }
  }

  async function createPoll(title, options) {
    const created = await api.createPoll(title, options)
    polls.value = [created, ...polls.value]
    return created
  }

  // 乐观更新：先本地改，界面立刻响应；失败则回滚到快照。
  async function vote(pollId, optionId) {
    const snapshot = polls.value
    const target = polls.value.find((poll) => poll.id === pollId)
    if (!target) return

    const previousId = target.votedOptionId
    if (previousId === optionId) return

    polls.value = polls.value.map((poll) => {
      if (poll.id !== pollId) return poll
      return {
        ...poll,
        votedOptionId: optionId,
        options: poll.options.map((option) => {
          if (option.id === optionId) return { ...option, votes: option.votes + 1 }
          if (option.id === previousId) return { ...option, votes: Math.max(0, option.votes - 1) }
          return option
        })
      }
    })

    markPending(pollId, true)
    try {
      replacePoll(await api.vote(pollId, optionId))
      error.value = null
    } catch (err) {
      polls.value = snapshot
      error.value = err instanceof ApiError ? err.message : '投票失败'
      throw err
    } finally {
      markPending(pollId, false)
    }
  }

  async function removePoll(pollId) {
    const snapshot = polls.value
    polls.value = polls.value.filter((poll) => poll.id !== pollId)
    try {
      await api.deletePoll(pollId)
    } catch (err) {
      polls.value = snapshot
      error.value = err instanceof ApiError ? err.message : '删除失败'
      throw err
    }
  }

  const totalVotes = computed(() =>
    polls.value.reduce(
      (sum, poll) => sum + poll.options.reduce((inner, option) => inner + option.votes, 0),
      0
    )
  )

  return {
    polls,
    loading,
    error,
    pendingPollIds,
    totalVotes,
    refresh,
    createPoll,
    vote,
    removePoll
  }
}
